import { BRAND } from "@/lib/config/brand";
import {
  getReturnButtonLabel,
  getSafeReturnTo,
} from "@/lib/navigation/publicReturnTo";
import {
  SOKO_GRID_EMAIL,
  SOKO_GRID_EMAIL_URL,
  SOKO_GRID_WEBSITE_DISPLAY,
  SOKO_GRID_WEBSITE_URL,
} from "@/src/config/contact";
import type { LegalPolicy } from "@/src/content/legalPolicies";
import { router, useLocalSearchParams } from "expo-router";
import { ArrowLeft, ExternalLink, Mail } from "lucide-react-native";
import React, { useCallback, useMemo } from "react";
import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

type Props = {
  policy: LegalPolicy;
};

function goToReturnPath(returnTo: string) {
  if (returnTo) {
    router.push(returnTo as any);
    return;
  }

  router.push("/" as any);
}

async function openUrl(url: string) {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.location.href = url;
    return;
  }

  await Linking.openURL(url);
}

export default function LegalPage({ policy }: Props) {
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams<{ returnTo?: string | string[] }>();
  const returnTo = useMemo(
    () => getSafeReturnTo(params.returnTo),
    [params.returnTo]
  );
  const isNarrow = width < 430;

  const handleBack = useCallback(() => {
    goToReturnPath(returnTo);
  }, [returnTo]);

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView
        style={styles.screen}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: Math.max(insets.bottom + 44, 72) },
        ]}
        showsVerticalScrollIndicator
      >
        <View style={styles.container}>
          <View style={styles.header}>
            <Text style={[styles.title, isNarrow ? styles.titleNarrow : null]}>
              {policy.title}
            </Text>
            <Text style={styles.effectiveDate}>
              Effective date: {policy.effectiveDate}
            </Text>

            {policy.intro.map((paragraph) => (
              <Text key={paragraph} style={styles.intro}>
                {paragraph}
              </Text>
            ))}
          </View>

          <View style={styles.contentCard}>
            {policy.sections.map((section, index) => (
              <View
                key={section.heading}
                style={index === 0 ? styles.sectionFirst : styles.section}
              >
                <Text style={styles.sectionHeading}>{section.heading}</Text>

                {section.paragraphs?.map((paragraph) => (
                  <Text key={paragraph} style={styles.bodyText}>
                    {paragraph}
                  </Text>
                ))}

                {section.bullets ? (
                  <View style={styles.bulletList}>
                    {section.bullets.map((bullet) => (
                      <View key={bullet} style={styles.bulletRow}>
                        <Text style={styles.bulletMarker}>-</Text>
                        <Text style={styles.bulletText}>{bullet}</Text>
                      </View>
                    ))}
                  </View>
                ) : null}

                {section.afterBullets?.map((paragraph) => (
                  <Text key={paragraph} style={styles.bodyText}>
                    {paragraph}
                  </Text>
                ))}
              </View>
            ))}
          </View>

          <View style={styles.contactCard}>
            <Text style={styles.contactTitle}>Soko Grid Ltd contact</Text>

            <View style={styles.contactRows}>
              <View style={styles.contactRow}>
                <Text style={styles.contactLabel}>Company</Text>
                <Text style={styles.contactValue}>{BRAND.legalName}</Text>
              </View>

              <View style={styles.divider} />

              <View style={styles.contactRow}>
                <Text style={styles.contactLabel}>Website</Text>
                <Text style={styles.contactValue}>
                  {SOKO_GRID_WEBSITE_DISPLAY}
                </Text>
              </View>

              <View style={styles.divider} />

              <View style={styles.contactRow}>
                <Text style={styles.contactLabel}>Email</Text>
                <Text style={styles.contactValue}>{SOKO_GRID_EMAIL}</Text>
              </View>
            </View>
          </View>

          <View style={styles.actions}>
            <Pressable
              accessibilityRole="button"
              onPress={() => openUrl(SOKO_GRID_EMAIL_URL)}
              style={({ pressed }) => [
                styles.primaryButton,
                isNarrow ? styles.narrowButton : null,
                pressed ? styles.pressed : null,
              ]}
            >
              <Mail size={18} color="#ffffff" strokeWidth={2.5} />
              <Text style={styles.primaryButtonText}>Email Us</Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              onPress={() => openUrl(SOKO_GRID_WEBSITE_URL)}
              style={({ pressed }) => [
                styles.secondaryButton,
                isNarrow ? styles.narrowButton : null,
                pressed ? styles.pressed : null,
              ]}
            >
              <ExternalLink size={18} color="#0f172a" strokeWidth={2.4} />
              <Text style={styles.secondaryButtonText}>Open Website</Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              onPress={handleBack}
              style={({ pressed }) => [
                styles.secondaryButton,
                isNarrow ? styles.narrowButton : null,
                pressed ? styles.pressed : null,
              ]}
            >
              <ArrowLeft size={18} color="#0f172a" strokeWidth={2.4} />
              <Text style={styles.secondaryButtonText}>
                {getReturnButtonLabel(returnTo)}
              </Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },
  screen: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingTop: 24,
  },
  container: {
    width: "100%",
    maxWidth: 820,
    alignSelf: "center",
    minWidth: 0,
  },
  header: {
    width: "100%",
  },
  title: {
    fontSize: 34,
    lineHeight: 40,
    fontWeight: "900",
    color: "#0f172a",
  },
  titleNarrow: {
    fontSize: 30,
    lineHeight: 36,
  },
  effectiveDate: {
    alignSelf: "flex-start",
    marginTop: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#bbf7d0",
    backgroundColor: "#ecfdf5",
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "900",
    color: "#166534",
  },
  intro: {
    marginTop: 12,
    maxWidth: 720,
    fontSize: 16,
    lineHeight: 25,
    fontWeight: "700",
    color: "#475569",
  },
  contentCard: {
    marginTop: 22,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 18,
  },
  sectionFirst: {
    paddingTop: 0,
  },
  section: {
    borderTopWidth: 1,
    borderTopColor: "#eef2f7",
    marginTop: 18,
    paddingTop: 18,
  },
  sectionHeading: {
    fontSize: 18,
    lineHeight: 24,
    fontWeight: "900",
    color: "#0f172a",
    marginBottom: 8,
  },
  bodyText: {
    marginTop: 6,
    fontSize: 15,
    lineHeight: 23,
    fontWeight: "700",
    color: "#475569",
  },
  bulletList: {
    marginTop: 10,
    gap: 8,
  },
  bulletRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  bulletMarker: {
    width: 12,
    fontSize: 15,
    lineHeight: 22,
    fontWeight: "900",
    color: "#0f766e",
  },
  bulletText: {
    flex: 1,
    minWidth: 0,
    fontSize: 15,
    lineHeight: 22,
    fontWeight: "700",
    color: "#475569",
  },
  contactCard: {
    marginTop: 18,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 18,
  },
  contactTitle: {
    fontSize: 18,
    lineHeight: 24,
    fontWeight: "900",
    color: "#0f172a",
  },
  contactRows: {
    marginTop: 14,
  },
  contactRow: {
    gap: 4,
  },
  contactLabel: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "900",
    color: "#64748b",
    textTransform: "uppercase",
  },
  contactValue: {
    fontSize: 16,
    lineHeight: 23,
    fontWeight: "900",
    color: "#111827",
  },
  divider: {
    height: 1,
    backgroundColor: "#edf2f7",
    marginVertical: 13,
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 20,
  },
  primaryButton: {
    minHeight: 46,
    borderRadius: 8,
    backgroundColor: "#0f172a",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 18,
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#ffffff",
  },
  secondaryButton: {
    minHeight: 46,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 18,
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#0f172a",
  },
  narrowButton: {
    width: "100%",
  },
  pressed: {
    opacity: 0.82,
  },
});
